import { useEffect, useRef, useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import { Button } from 'shared/components/ui/button'

/**
 * The phone-camera scanning mode of the scan control (ADR-0010: one responsive
 * build serves both hardware profiles). It opens the camera, watches the frames
 * for a barcode, and reports the first code it reads through `onResult` — the
 * SAME funnel the keyboard-wedge and manual paths use, so every hardware profile
 * reaches one resolution call.
 *
 * Codes are read by the browser's own `BarcodeDetector` where it has a working one
 * (Android Chrome); elsewhere — every browser on iPhone — by a WebAssembly decoder loaded
 * only when the camera opens, served by the app itself rather than a CDN.
 *
 * Camera access needs HTTPS; the browser enforces that, and the app is served
 * over HTTPS for exactly this reason (ADR-0010). Where the camera is
 * unavailable, this degrades to a stated message rather than a dead control —
 * manual entry stays available beside it. The detection engine is not
 * unit-tested against real hardware; the scan control injects a fake in tests
 * and drives `onResult` directly (spec Testing Decisions).
 */

export interface CameraScannerProps {
	onResult: (code: string) => void
	onClose: () => void
}

/** The subset of the DOM `BarcodeDetector` this control relies on. */
interface BarcodeDetectorLike {
	detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>
}
type BarcodeDetectorCtor = new () => BarcodeDetectorLike
type NativeBarcodeDetector = BarcodeDetectorCtor & {
	getSupportedFormats(): Promise<string[]>
}

type CameraSupport = 'ok' | 'insecure' | 'no-camera'

function cameraSupport(): CameraSupport {
	// Over plain http (a phone on the dev server's LAN address) the browser hides
	// `mediaDevices` altogether, so this is told apart from a phone without a camera.
	if (!globalThis.isSecureContext) return 'insecure'
	return typeof navigator.mediaDevices?.getUserMedia === 'function'
		? 'ok'
		: 'no-camera'
}

let wasmDetector: Promise<BarcodeDetectorCtor> | null = null

function loadWasmDetector(): Promise<BarcodeDetectorCtor> {
	wasmDetector ??= Promise.all([
		import('barcode-detector/ponyfill'),
		import('zxing-wasm/reader/zxing_reader.wasm?url'),
	]).then(([{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }]) => {
		prepareZXingModule({
			overrides: {
				locateFile: (path: string, prefix: string) =>
					path.endsWith('.wasm') ? wasmUrl : prefix + path,
			},
		})
		return BarcodeDetector
	})
	return wasmDetector
}

async function createDetector(): Promise<BarcodeDetectorLike> {
	const Native = (
		globalThis as unknown as { BarcodeDetector?: NativeBarcodeDetector }
	).BarcodeDetector
	// Android Chrome decodes through Google Play services; on a phone without them
	// the detector exists but supports no format and never reads a code.
	const formats = Native
		? await Native.getSupportedFormats().catch(() => [])
		: []
	if (Native && formats.length > 0) return new Native()
	const Detector = await loadWasmDetector()
	return new Detector()
}

export function CameraScanner({ onResult, onClose }: CameraScannerProps) {
	const { t } = useTranslate()
	const videoRef = useRef<HTMLVideoElement>(null)
	// Support is a property of the environment, decided once at first render, so
	// the "unsupported" message is derived state — never a setState in an effect.
	const [support] = useState(cameraSupport)
	const [error, setError] = useState<string | null>(null)

	// Hold `onResult` in a ref so the camera-session effect depends only on
	// support: the parent re-creates the funnel each render, and we must not tear
	// down and restart the camera stream on every parent render.
	const onResultRef = useRef(onResult)
	useEffect(() => {
		onResultRef.current = onResult
	}, [onResult])
	const errorText = t('parcels.scanCameraError')

	useEffect(() => {
		if (support !== 'ok') return

		let stream: MediaStream | null = null
		let raf = 0
		let cancelled = false
		let detector: BarcodeDetectorLike

		const tick = async () => {
			if (cancelled) return
			const video = videoRef.current
			if (video && video.readyState >= 2) {
				try {
					const codes = await detector.detect(video)
					const first = codes.find((c) => c.rawValue.trim().length > 0)
					if (first) {
						onResultRef.current(first.rawValue)
						return
					}
				} catch {
					// A transient decode error on one frame is ignored; the next frame
					// is tried. A fatal setup failure is caught in `start` below.
				}
			}
			raf = requestAnimationFrame(() => void tick())
		}

		const start = async () => {
			try {
				// The camera is asked for first, so the permission prompt is not held
				// back by the decoder download.
				stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: 'environment' },
				})
				if (cancelled) return
				const video = videoRef.current
				if (video) {
					video.srcObject = stream
					await video.play()
				}
				detector = await createDetector()
				if (cancelled) return
				raf = requestAnimationFrame(() => void tick())
			} catch {
				if (!cancelled) setError(errorText)
			}
		}
		void start()

		return () => {
			cancelled = true
			cancelAnimationFrame(raf)
			stream?.getTracks().forEach((track) => track.stop())
		}
	}, [support, errorText])

	const message =
		support === 'insecure'
			? t('parcels.scanCameraInsecure')
			: support === 'no-camera'
				? t('parcels.scanUnsupported')
				: error

	return (
		<div className='flex flex-col gap-2 rounded-lg border p-3'>
			{message ? (
				<p className='text-sm text-muted-foreground'>{message}</p>
			) : (
				<>
					<video
						ref={videoRef}
						className='w-full max-w-sm rounded-md bg-black'
						muted
						playsInline
					/>
					<p className='text-xs text-muted-foreground'>
						{t('parcels.scanCameraHint')}
					</p>
				</>
			)}
			<div>
				<Button type='button' variant='ghost' size='sm' onClick={onClose}>
					{t('parcels.scanCameraClose')}
				</Button>
			</div>
		</div>
	)
}
